// Consent record rules (spec 2026-09-14-meta-ads-end-to-end-tracking-design §4.1).
// Pure: no DOM, no Nuxt. Unit tested in tests/tracking/consentRecord.test.ts.

export type ConsentRecord = {
  analytics: boolean;
  marketing: boolean;
  region: string;
  gpc: boolean;
  decided: boolean;
  decidedAtSec: number;
};

export type AdConsent = 'granted' | 'denied' | 'unknown';

export type RegionResolution = { country: string; consentRequired: boolean };

export const CONSENT_COOKIE = 'w369_consent';
export const GEO_COOKIE = 'w369_geo';
export const CONSENT_MAX_AGE_SEC = 31536000;
const MAX_DECIDED_AT_SEC = 9999999999;

// EU (27) + EEA (IS, LI, NO) + UK + Switzerland, plus EU outermost regions/special
// territories that geo databases often report as their own country code (AX Åland,
// RE Réunion, GP Guadeloupe, MQ Martinique, GF French Guiana, YT Mayotte, MF Saint
// Martin) and the Crown Dependencies/Gibraltar, which follow UK/EU-adjacent rules
// (GI Gibraltar, JE Jersey, GG Guernsey, IM Isle of Man).
const CONSENT_REGION_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO', 'GB', 'CH',
  'AX', 'RE', 'GP', 'MQ', 'GF', 'YT', 'MF', 'GI', 'JE', 'GG', 'IM',
]);

// Time zones outside the Europe/* prefix that still sit in or report for a consent
// region (Cyprus, Ceuta/Melilla, Atlantic/Indian island territories, French overseas
// departments). Kept separate from Europe/* so the startsWith check stays cheap.
// Some entries are deliberate over-inclusion rather than a strict EU/EEA read: the
// Faroes (Atlantic/Faroe and the browser-reported Atlantic/Faeroe spelling),
// St Barthélemy and Svalbard (Arctic/Longyearbyen) are not themselves EU/EEA consent
// regions, but this list would rather ask for consent too often than too rarely.
const TZ_CONSENT_REQUIRED_EXTRA = new Set([
  'Asia/Nicosia', 'Asia/Famagusta', 'Africa/Ceuta', 'Atlantic/Reykjavik', 'Atlantic/Canary', 'Atlantic/Madeira',
  'Atlantic/Azores', 'Atlantic/Faroe', 'Atlantic/Faeroe', 'Indian/Reunion', 'Indian/Mayotte', 'America/Guadeloupe',
  'America/Martinique', 'America/Cayenne', 'America/Marigot', 'America/St_Barthelemy', 'Arctic/Longyearbyen',
]);

export function isConsentRegion(country: string): boolean {
  return CONSENT_REGION_CODES.has(country.toUpperCase());
}

// Missing/empty, UTC, GMT and Etc/* are treated as consent-required: privacy-hardened
// browsers (and some bots) report these instead of a real zone, and we deliberately
// over-include rather than risk skipping consent for an EU visitor.
function isConsentRequiredTimeZone(timeZone: string | undefined): boolean {
  const zone = (timeZone || '').trim();
  if (!zone) return true;
  if (zone === 'UTC' || zone === 'GMT') return true;
  if (zone.startsWith('Etc/')) return true;
  if (zone.startsWith('Europe/')) return true;
  return TZ_CONSENT_REQUIRED_EXTRA.has(zone);
}

export function resolveRegion(geoCookie: string | undefined, timeZone: string | undefined): RegionResolution {
  const country = (geoCookie || '').trim().toUpperCase();
  // 'XX' and 'EU' are placeholder/unknown codes some geo databases emit, not real
  // country codes — treat them the same as no cookie at all.
  const isKnownCode = /^[A-Z]{2}$/.test(country) && country !== 'XX' && country !== 'EU';
  if (isKnownCode) return { country, consentRequired: isConsentRegion(country) };
  return { country: 'XX', consentRequired: isConsentRequiredTimeZone(timeZone) };
}

export function serializeConsent(record: ConsentRecord): string {
  const bit = (value: boolean) => (value ? '1' : '0');
  const region = record.region.toUpperCase();
  const seconds = Number.isFinite(record.decidedAtSec)
    ? Math.min(MAX_DECIDED_AT_SEC, Math.max(0, Math.floor(record.decidedAtSec)))
    : 0;
  return [
    'v2',
    bit(record.analytics),
    bit(record.marketing),
    /^[A-Z]{2}$/.test(region) ? region : 'XX',
    bit(record.gpc),
    bit(record.decided),
    String(seconds),
  ].join('.');
}

export function parseConsent(raw: string | null | undefined): ConsentRecord | null {
  if (!raw) return null;
  let text: string;
  try {
    text = decodeURIComponent(raw);
  } catch {
    return null;
  }
  const parts = text.split('.');
  if (parts.length !== 7 || parts[0] !== 'v2') return null;
  const [, analyticsBit, marketingBit, region, gpcBit, decidedBit, seconds] = parts;
  const bit = (value: string) => (value === '1' ? true : value === '0' ? false : null);
  const analytics = bit(analyticsBit);
  const marketing = bit(marketingBit);
  const gpc = bit(gpcBit);
  const decided = bit(decidedBit);
  if (analytics === null || marketing === null || gpc === null || decided === null) return null;
  if (!/^[A-Z]{2}$/.test(region)) return null;
  if (!/^\d{1,10}$/.test(seconds)) return null;
  return { analytics, marketing, region, gpc, decided, decidedAtSec: Number(seconds) };
}

export function defaultConsent(region: RegionResolution, gpc: boolean, nowSec: number): ConsentRecord | null {
  if (region.consentRequired) return null;
  return { analytics: true, marketing: !gpc, region: region.country, gpc, decided: false, decidedAtSec: nowSec };
}

export function applyGpc(record: ConsentRecord, gpc: boolean): ConsentRecord {
  return gpc ? { ...record, marketing: false, gpc: true } : record;
}

export function adConsentOf(record: ConsentRecord | null): AdConsent {
  if (!record) return 'unknown';
  return record.marketing && !record.gpc ? 'granted' : 'denied';
}

// A default record written outside a consent region is not a decision — it is a
// permissive placeholder. If the visitor is later (re)resolved into a consent
// region while that placeholder is still undecided, it must not be treated as
// consent for ads/analytics; callers should see null (i.e. "ask again") instead.
//
// A *decided* record made outside a consent region is different, and splits in
// two. A full reject (nothing granted) needs no consent to honour, so it stays
// effective everywhere, including once the visitor is inside a consent region —
// a CCPA-style opt-out must never be silently erased by a later EU/UK visit. A
// decided record that grants something (analytics and/or marketing) is not an
// EU/UK consent choice, though: once the visitor resolves into a consent region
// it is hidden — this returns null so the banner asks again and no tag fires —
// but it is NOT discarded the way a stale undecided default is. resolveConsentOnLoad
// tells the two null-producing cases apart (see isStaleDiscard) and leaves a
// hidden record's cookie/mirror alone, so the original choice is honoured again
// once the visitor is back in its own region. A record whose region is the
// unresolved placeholder 'XX' (what consent-region visitors get when the geo
// cookie is missing) is always treated as if it matched, so it never gets hidden
// or loops the banner.
export function effectiveConsent(record: ConsentRecord | null, region: RegionResolution): ConsentRecord | null {
  if (!record) return null;
  if (!region.consentRequired) return record;
  if (!record.decided) return null;
  if (record.region === 'XX' || isConsentRegion(record.region)) return record;
  if (!record.analytics && !record.marketing) return record; // full reject: always honoured
  return null; // grants something outside a consent region: hidden, not discarded
}

export function cookieDomainFor(hostname: string): string | null {
  return hostname === 'workspace369.com' || hostname.endsWith('.workspace369.com') ? '.workspace369.com' : null;
}

export function buildCookie(name: string, value: string, maxAgeSec: number, hostname: string, secure: boolean): string {
  const domain = cookieDomainFor(hostname);
  return [
    `${name}=${encodeURIComponent(value)}`,
    'Path=/',
    `Max-Age=${maxAgeSec}`,
    'SameSite=Lax',
    domain ? `Domain=${domain}` : '',
    secure ? 'Secure' : '',
  ]
    .filter(Boolean)
    .join('; ');
}

// Every value for `name` in header order. A malformed or duplicated cookie header
// (e.g. a stale host-only cookie shadowed by a newer domain-scoped one) can carry
// the same name more than once; callers that need "the real one" should try each
// value in turn rather than trusting the first.
export function readCookieValues(cookieHeader: string, name: string): string[] {
  const values: string[] = [];
  for (const part of cookieHeader.split(';')) {
    const [key, ...rest] = part.trim().split('=');
    if (key === name) values.push(rest.join('='));
  }
  return values;
}

export function readCookie(cookieHeader: string, name: string): string | undefined {
  return readCookieValues(cookieHeader, name)[0];
}

// Picks the first w369_consent value that both parses as a valid v2 record and is
// decided — an actual choice always wins over an undecided default (e.g. one
// written before geo resolved) even if the default appears earlier in the header.
// Falls back to the first parseable value if none is decided, and skips entirely
// over stale/legacy values (e.g. a leftover host-only `decided` cookie) that don't
// parse at all. Returns null only when nothing in the header parses.
export function readConsentCookie(cookieHeader: string): ConsentRecord | null {
  let firstParsed: ConsentRecord | null = null;
  for (const value of readCookieValues(cookieHeader, CONSENT_COOKIE)) {
    const parsed = parseConsent(value);
    if (!parsed) continue;
    if (parsed.decided) return parsed;
    if (!firstParsed) firstParsed = parsed;
  }
  return firstParsed;
}

export type ConsentLoadInput = {
  cookieHeader: string;
  storedValue: string | null; // localStorage mirror
  geoCookie: string | undefined;
  timeZone: string | undefined;
  gpc: boolean;
  nowSec: number;
  hostname: string;
};

export type ConsentAction =
  | { type: 'expireLegacyHostCookie' }
  | { type: 'forget' }
  | { type: 'write'; record: ConsentRecord; maxAgeSec: number };

export type ConsentLoadResult = { region: RegionResolution; consent: ConsentRecord | null; actions: ConsentAction[] };

// A day of clock skew is tolerated; beyond that a "decided in the future"
// timestamp is clearly bogus (tampering, a broken clock) and must not be able
// to win a merge against a real, present-dated record.
const FUTURE_TOLERANCE_SEC = 86400;

// Picks the cookie value or the localStorage mirror as the load candidate: a
// decided record always beats an undecided default regardless of source; if both
// are decided, the more recent decision normally wins; otherwise (including
// "both undecided") the cookie wins, since it's what the server and the app
// already read as the source of truth.
//
// P10b hardening: when both are decided and disagree on `marketing`, a plain
// "newest wins" is a coin flip if the two timestamps are within a day of each
// other (near-simultaneous writes to two stores, e.g. one arriving from an
// in-flight request) or if the nominally-newer one is dated after `nowSec`
// (still future but inside FUTURE_TOLERANCE_SEC, so not discarded outright by
// resolveFutureDated) — in either case the decidedAtSec ordering isn't a
// trustworthy tiebreaker. Prefer the opt-out (marketing === false): losing a
// grant to timestamp noise costs nothing; losing an opt-out would violate it.
function pickCandidate(
  fromCookie: ConsentRecord | null,
  fromStorage: ConsentRecord | null,
  nowSec: number,
): ConsentRecord | null {
  if (fromCookie && fromStorage) {
    if (fromCookie.decided && fromStorage.decided) {
      const newer = fromStorage.decidedAtSec > fromCookie.decidedAtSec ? fromStorage : fromCookie;
      if (fromCookie.marketing !== fromStorage.marketing) {
        const closeInTime = Math.abs(fromStorage.decidedAtSec - fromCookie.decidedAtSec) <= FUTURE_TOLERANCE_SEC;
        const newerIsFuture = newer.decidedAtSec > nowSec;
        if (closeInTime || newerIsFuture) return fromCookie.marketing === false ? fromCookie : fromStorage;
      }
      return newer;
    }
    if (fromCookie.decided !== fromStorage.decided) {
      return fromCookie.decided ? fromCookie : fromStorage;
    }
    return fromCookie;
  }
  return fromCookie || fromStorage || null;
}

function isFutureInvalid(record: ConsentRecord, nowSec: number): boolean {
  return record.decidedAtSec > nowSec + FUTURE_TOLERANCE_SEC;
}

// A decided record with marketing off never truly expires — CCPA-style opt-outs
// don't have a shelf life. Used both for future-date clamping and for the age
// check below; see resolveConsentOnLoad's rule comment.
function isOptOut(record: ConsentRecord): boolean {
  return record.decided && !record.marketing;
}

// effectiveConsent returns null for two different reasons, and resolveConsentOnLoad
// needs to tell them apart: a stale undecided default is genuinely discarded
// (forget), while a decided record that's merely hidden outside its consent
// region must be left in the cookie/mirror so it's honoured again back home.
// The two are told apart by `decided` alone — per effectiveConsent, the only way
// a *decided* record nulls out is the hidden-grant case, since a decided full
// reject or an XX/consent-region record is always returned as-is.
function isStaleDiscard(record: ConsentRecord): boolean {
  return !record.decided;
}

function clampMaxAgeSec(maxAgeSec: number): number {
  return Math.min(CONSENT_MAX_AGE_SEC, Math.max(1, maxAgeSec));
}

// Pure resolution of "what should the consent store do on load" — no DOM, no
// cookie/localStorage writes. The composable applies the returned actions.
// Rules:
// 1. A legacy host-only w369_consent cookie is expired first, on every
//    workspace369 host (it would otherwise shadow the shared .workspace369.com one).
// 2. Resolve the region from the geo cookie / time zone.
// 3. A record dated more than a day in the future can't win the merge as-is. An
//    opt-out (decided, marketing off) is clamped to now and kept — clock skew
//    shouldn't cost someone their CCPA opt-out; anything else (a grant, or
//    undecided) is discarded as if absent.
// 4. Pick a load candidate between the (future-resolved) cookie and localStorage
//    mirror. If a future-date discard left nothing, forget — but only then: a
//    future-dated record in one store must never wipe a valid candidate that
//    survives in the other.
// 5. A candidate older than CONSENT_MAX_AGE_SEC is normally treated as absent
//    (the cookie policy promises to ask again after a year) and forgotten — but
//    an opt-out never ages out: it's kept, and its storage lifetime gets
//    refreshed below instead of being forgotten (on every load past the
//    threshold — a sliding refresh, see the comment at the age check). An aged
//    1/0 opt-out additionally has its analytics grant downgraded to a full
//    reject when the visitor's or the record's own region requires consent
//    (owner decision: EU/UK analytics consent still expires after a year).
// 6. Run the candidate through effectiveConsent for the current region. A stale
//    undecided default that becomes ineligible is forgotten (never twice); a
//    decided record merely hidden outside its consent region is left alone.
// 7. An effective (shown) record is written back (GPC-adjusted, or re-defaulted
//    if it was still undecided) whenever it differs from what the cookie OR the
//    localStorage mirror already holds, or whenever it just got age-refreshed —
//    comparing by value, so a stale mirror can't resurrect an older choice
//    later, and GPC state alone doesn't rewrite on every load once both stores
//    already agree.
// 8. A hidden (not shown, not discarded) record is written back unchanged when
//    it just got age-refreshed, or when a future-dated sibling was just
//    discarded and this store needs healing — never merely because the mirrors
//    disagree (that would defeat "hidden").
// 9. No candidate survives: write a fresh default outside consent regions, or
//    leave consent null inside one (ask again).
export function resolveConsentOnLoad(input: ConsentLoadInput): ConsentLoadResult {
  const actions: ConsentAction[] = [];
  if (cookieDomainFor(input.hostname)) actions.push({ type: 'expireLegacyHostCookie' });

  const region = resolveRegion(input.geoCookie, input.timeZone);

  const fromCookieRaw = readConsentCookie(input.cookieHeader);
  const fromStorageRaw = parseConsent(input.storedValue);

  let forgotten = false;
  const forgetOnce = () => {
    if (forgotten) return;
    actions.push({ type: 'forget' });
    forgotten = true;
  };

  let anyFutureDiscarded = false;
  const resolveFutureDated = (record: ConsentRecord | null): ConsentRecord | null => {
    if (!record || !isFutureInvalid(record, input.nowSec)) return record;
    if (isOptOut(record)) return { ...record, decidedAtSec: input.nowSec };
    anyFutureDiscarded = true;
    return null;
  };
  const fromCookie = resolveFutureDated(fromCookieRaw);
  const fromStorage = resolveFutureDated(fromStorageRaw);

  let candidate = pickCandidate(fromCookie, fromStorage, input.nowSec);

  // A future-dated record in one store must not wipe a valid candidate
  // surviving in the other — forget only when nothing valid is left.
  if (anyFutureDiscarded && !candidate) forgetOnce();

  // An opt-out never ages out — but "never ages out" only protects the
  // marketing===false it was actually decided about. An EU/UK visitor's
  // *analytics* consent still has a one-year shelf life (owner decision), so a
  // decided 1/0 aged past a year is downgraded to a full 0/0 reject once either
  // the visitor's own region requires consent, or the record's own region does
  // (XX or a consent region) — i.e. whenever analytics=true would otherwise need
  // asking again. A US 1/0 record loaded from the US needs no such downgrade:
  // US analytics needs no consent, so it's left exactly as decided. The rewrite
  // keeps region/gpc/decidedAtSec and stays effective as a full reject (see
  // effectiveConsent) rather than being hidden.
  //
  // Deliberately re-evaluated (and rewritten back) on every single load past the
  // threshold, not just once: this is a sliding refresh, not a one-time
  // conversion. It keeps the browser cookie's Max-Age from ever running out on
  // an opt-out, and gives the app/backend the longest-lived record of it.
  let neverExpiredRefresh = false;
  if (candidate && input.nowSec - candidate.decidedAtSec >= CONSENT_MAX_AGE_SEC) {
    if (isOptOut(candidate)) {
      neverExpiredRefresh = true;
      if (
        candidate.analytics &&
        (region.consentRequired || candidate.region === 'XX' || isConsentRegion(candidate.region))
      ) {
        candidate = { ...candidate, analytics: false };
      }
    } else {
      candidate = null;
      forgetOnce();
    }
  }

  const effective = effectiveConsent(candidate, region);
  if (candidate && !effective && isStaleDiscard(candidate)) forgetOnce();

  if (effective) {
    const record = effective.decided
      ? applyGpc(effective, input.gpc)
      : (defaultConsent(region, input.gpc, effective.decidedAtSec) ?? effective);
    const cookieDiffers = !fromCookieRaw || serializeConsent(record) !== serializeConsent(fromCookieRaw);
    const storageDiffers = input.storedValue == null || serializeConsent(record) !== input.storedValue;
    if (cookieDiffers || storageDiffers || neverExpiredRefresh) {
      const maxAgeSec = neverExpiredRefresh
        ? CONSENT_MAX_AGE_SEC
        : clampMaxAgeSec(CONSENT_MAX_AGE_SEC - (input.nowSec - record.decidedAtSec));
      actions.push({ type: 'write', record, maxAgeSec });
    }
    return { region, consent: record, actions };
  }

  if (candidate && !isStaleDiscard(candidate) && (neverExpiredRefresh || anyFutureDiscarded)) {
    const maxAgeSec = neverExpiredRefresh
      ? CONSENT_MAX_AGE_SEC
      : clampMaxAgeSec(CONSENT_MAX_AGE_SEC - (input.nowSec - candidate.decidedAtSec));
    actions.push({ type: 'write', record: candidate, maxAgeSec });
    return { region, consent: null, actions };
  }

  const fallback = defaultConsent(region, input.gpc, input.nowSec);
  if (fallback) actions.push({ type: 'write', record: fallback, maxAgeSec: CONSENT_MAX_AGE_SEC });
  return { region, consent: fallback, actions };
}
